"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  LABEL,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  EQUATION_ERROR_STRINGS,
  EquationView,
  equationErrorMessage,
  equationText,
} from "@/components/tools/chem-reactions/equation-view";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  atomCheck,
  type BalanceOutcome,
  balance,
  buildMatrix,
  CHARGE_ROW,
  orientEquation,
  type ParsedEquation,
  parseEquation,
} from "@/lib/tool-kit/chem-reactions/balance";
import {
  fractionText,
  lcmBig,
  ONE,
} from "@/lib/tool-kit/chem-reactions/rational";

/** English strings. A translated content file overrides these through `ui`. */
export const CHEMICAL_EQUATION_BALANCER_STRINGS = {
  equationLabel: "Unbalanced equation",
  equationHint:
    "Separate species with +, and reactants from products with ->, → or =. Write ions with a sign (Fe3+, SO4^2-, OH-) and electrons as e-. Coefficients and (s)/(aq) labels you type are ignored.",
  equationPlaceholder: "C3H8 + O2 -> CO2 + H2O",
  tryLabel: "Try an example",
  results: "Result",
  balanced: "Balanced equation",
  copy: "Copy equation",
  copied: "Copied!",
  ignoredNote:
    "The coefficients you typed were ignored: the balancer works out its own from the atoms.",
  coefficientsHeading: "Coefficients",
  columnSpecies: "Species",
  columnSide: "Side",
  columnCoefficient: "Coefficient",
  sideReactant: "Reactant",
  sideProduct: "Product",
  checkHeading: "Atom check",
  columnItem: "Element",
  columnLeft: "Left side",
  columnRight: "Right side",
  chargeRow: "Charge",
  checkNote: "Every row matches, so atoms and charge are conserved.",
  workingHeading: "The working",
  matrixIntro:
    "Each column is a species and each row an element. Reactant atoms count as positive and product atoms as negative, so coefficients balance the equation exactly when every row sums to zero.",
  columnRow: "Row",
  vectorLine: "Null-space vector: ({vector})",
  scaleLine: "Multiply by {factor} to clear fractions: ({whole})",
  swappedHeading: "Balanced with some species on the other side",
  swappedBody:
    "As written, these species cannot all have positive coefficients. The only balance puts {species} on the opposite side of the arrow, which usually means the equation is written backwards or a formula is wrong.",
  idleHeading: "Some species take no part",
  idleBody:
    "{species} gets a coefficient of zero: the rest of the equation balances without it. Check whether it belongs in this reaction.",
  noneHeading: "No balance exists",
  noneBody:
    "No positive set of coefficients makes the atoms and charge match, so the only solution is zero of everything.",
  oneSidedLine: "{element} appears only among the {side}.",
  sideReactants: "reactants",
  sideProducts: "products",
  noneTip:
    "Atoms cannot appear from nowhere, so add the missing species or correct the formulas. If the equation has ions, check each charge too.",
  manyHeading: "No unique balance",
  manyBody:
    "{count} independent reactions are tangled together here, so the coefficients are not unique: any mix of them balances. Each line below is one of those reactions (a species with a negative coefficient has moved to the other side). Split the equation into separate reactions, or drop a species, to get one answer.",
  manyItem: "Independent balance {number}",
  tooLarge:
    "The smallest whole-number coefficients are larger than 1,000,000, so this equation is probably not what you meant. Check the formulas.",
  errors: EQUATION_ERROR_STRINGS,
} as const;

type Strings = typeof CHEMICAL_EQUATION_BALANCER_STRINGS;

const EXAMPLES = [
  "C3H8 + O2 -> CO2 + H2O",
  "Fe + O2 -> Fe2O3",
  "KMnO4 + HCl -> KCl + MnCl2 + Cl2 + H2O",
  "MnO4- + Fe2+ + H+ -> Mn2+ + Fe3+ + H2O",
  "MnO4- + H+ + e- -> Mn2+ + H2O",
  "H2 + O2 -> H2O + H2O2",
] as const;

function MatrixWorking({
  equation,
  outcome,
  strings,
}: {
  equation: ParsedEquation;
  outcome: BalanceOutcome;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const matrix = buildMatrix(equation);
  const head = [
    strings.columnRow,
    ...equation.species.map((item) => item.label),
  ];
  const rows = matrix.rowKeys.map((key, index) => [
    key === CHARGE_ROW ? strings.chargeRow : key,
    ...matrix.cells[index].map((cell) => fmt(cell)),
  ]);
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      <p className="text-muted-foreground text-sm">{strings.matrixIntro}</p>
      <DataTable head={head} rows={rows} />
      {outcome.kind === "unique" ? (
        <div className={WORKING_BOX}>
          <p>
            {t(strings.vectorLine, {
              vector: outcome.vector.map(fractionText).join(", "),
            })}
          </p>
          <p>
            {t(strings.scaleLine, {
              factor: fmt(scaleFactor(outcome)),
              whole: outcome.coefficients.map((c) => fmt(c)).join(", "),
            })}
          </p>
        </div>
      ) : null}
    </div>
  );
}

function scaleFactor(outcome: Extract<BalanceOutcome, { kind: "unique" }>) {
  let factor = ONE.n;
  for (const cell of outcome.vector) {
    factor = lcmBig(factor, cell.d);
  }
  return Number(factor);
}

function UniqueResult({
  equation,
  coefficients,
  outcome,
  strings,
}: {
  equation: ParsedEquation;
  coefficients: readonly number[];
  outcome: Extract<BalanceOutcome, { kind: "unique" }>;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const oriented = orientEquation(equation, coefficients);
  const check = atomCheck(equation, oriented);
  const natural = oriented.moved.length === 0;
  return (
    <>
      <div className={RESULT_CARD}>
        <p className="font-medium text-muted-foreground text-sm">
          {natural ? strings.balanced : strings.swappedHeading}
        </p>
        <p className="mt-2 font-semibold text-2xl text-foreground leading-relaxed">
          <EquationView oriented={oriented} />
        </p>
      </div>
      {natural ? null : (
        <Notice>
          {t(strings.swappedBody, {
            species: oriented.moved.map((s) => s.label).join(", "),
          })}
        </Notice>
      )}
      {oriented.idle.length > 0 ? (
        <Notice>
          {t(strings.idleBody, {
            species: oriented.idle.map((s) => s.label).join(", "),
          })}
        </Notice>
      ) : null}
      {equation.ignoredCoefficients ? (
        <Notice>{strings.ignoredNote}</Notice>
      ) : null}
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={equationText(oriented)}
      />
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.coefficientsHeading}</h3>
        <DataTable
          head={[
            strings.columnSpecies,
            strings.columnSide,
            strings.columnCoefficient,
          ]}
          rows={equation.species.map((item, index) => [
            item.label,
            item.side === "reactant"
              ? strings.sideReactant
              : strings.sideProduct,
            fmt(coefficients[index]),
          ])}
        />
      </div>
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.checkHeading}</h3>
        <DataTable
          head={[strings.columnItem, strings.columnLeft, strings.columnRight]}
          rows={check.map((row) => [
            row.key === CHARGE_ROW ? strings.chargeRow : row.key,
            fmt(row.left),
            fmt(row.right),
          ])}
        />
        <p className="text-muted-foreground text-sm">{strings.checkNote}</p>
      </div>
      <MatrixWorking equation={equation} outcome={outcome} strings={strings} />
    </>
  );
}

function ManyResult({
  equation,
  outcome,
  strings,
}: {
  equation: ParsedEquation;
  outcome: Extract<BalanceOutcome, { kind: "many" }>;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  return (
    <>
      <Notice>
        <strong>{strings.manyHeading}.</strong>{" "}
        {t(strings.manyBody, { count: fmt(outcome.nullity) })}
      </Notice>
      <ul className="space-y-3">
        {outcome.basis.map((vector, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: the basis has no identity beyond its order
          <li key={index}>
            <p className={LABEL}>
              {t(strings.manyItem, { number: fmt(index + 1) })}
            </p>
            <p className="text-foreground text-lg">
              <EquationView oriented={orientEquation(equation, vector)} />
            </p>
          </li>
        ))}
      </ul>
      <MatrixWorking equation={equation} outcome={outcome} strings={strings} />
    </>
  );
}

function NoneResult({
  equation,
  outcome,
  strings,
}: {
  equation: ParsedEquation;
  outcome: Extract<BalanceOutcome, { kind: "none" }>;
  strings: Strings;
}) {
  return (
    <>
      <ErrorNotice>
        <strong>{strings.noneHeading}.</strong> {strings.noneBody}
      </ErrorNotice>
      {outcome.oneSided.length > 0 ? (
        <ul className="list-disc space-y-1 pl-6 text-foreground text-sm">
          {outcome.oneSided.map((item) => (
            <li key={item.key}>
              {t(strings.oneSidedLine, {
                element: item.key,
                side:
                  item.side === "reactant"
                    ? strings.sideReactants
                    : strings.sideProducts,
              })}
            </li>
          ))}
        </ul>
      ) : null}
      <p className="text-muted-foreground text-sm">{strings.noneTip}</p>
      <MatrixWorking equation={equation} outcome={outcome} strings={strings} />
    </>
  );
}

function Results({ text, strings }: { text: string; strings: Strings }) {
  const parsed = parseEquation(text);
  if (!parsed.ok) {
    if (parsed.error.code === "empty") {
      return <Notice>{strings.errors.empty}</Notice>;
    }
    return (
      <ErrorNotice>
        {equationErrorMessage(strings.errors, parsed.error)}
      </ErrorNotice>
    );
  }
  const outcome = balance(parsed.equation);
  if (outcome.kind === "unique") {
    return (
      <UniqueResult
        coefficients={outcome.coefficients}
        equation={parsed.equation}
        outcome={outcome}
        strings={strings}
      />
    );
  }
  if (outcome.kind === "many") {
    return (
      <ManyResult
        equation={parsed.equation}
        outcome={outcome}
        strings={strings}
      />
    );
  }
  if (outcome.kind === "none") {
    return (
      <NoneResult
        equation={parsed.equation}
        outcome={outcome}
        strings={strings}
      />
    );
  }
  return <ErrorNotice>{strings.tooLarge}</ErrorNotice>;
}

export default function ChemicalEquationBalancerWidget() {
  const strings = useToolUi(CHEMICAL_EQUATION_BALANCER_STRINGS);
  const [text, setText] = useState<string>(EXAMPLES[0]);
  return (
    <>
      <TextField
        hint={strings.equationHint}
        id="ceb-equation"
        label={strings.equationLabel}
        mono
        onChange={setText}
        placeholder={strings.equationPlaceholder}
        value={text}
      />
      <div className="flex flex-wrap items-center gap-2">
        <span className={LABEL}>{strings.tryLabel}</span>
        {EXAMPLES.map((example) => (
          <button
            className={BUTTON_SMALL}
            key={example}
            onClick={() => setText(example)}
            type="button"
          >
            {example}
          </button>
        ))}
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Results strings={strings} text={text} />
      </section>
    </>
  );
}
